<template>
  <!-- Информация о времени и адресе -->
  <div class="order-footer">
    <!-- Время оформления (поддержка обоих форматов) -->
    <div v-if="createdAt" class="info-row">
      <UIcon name="i-heroicons-clock" class="info-icon" />
      <div class="info-content">
        <span class="info-label">Время оформления</span>
        <div class="info-value">{{ formattedCreatedAt }}</div>
      </div>
    </div>

    <!-- Адрес доставки (поддержка обоих форматов) -->
    <div v-if="orderType === 'delivery'" class="info-row">
      <UIcon name="i-heroicons-map-pin" class="info-icon" />
      <div class="info-content">
        <span class="info-label">Адрес доставки</span>
        <div class="info-value">{{ deliveryAddress }}</div>
      </div>
    </div>

    <!-- Самовывоз -->
    <div v-else-if="orderType === 'takeaway' || orderType === 'pickup'" class="info-row">
      <UIcon name="i-heroicons-building-storefront" class="info-icon" />
      <div class="info-content">
        <span class="info-label">Самовывоз</span>
        <div class="info-value">{{ branchAddress }}</div>
      </div>
    </div>

    <!-- В зале -->
    <div v-else-if="orderType === 'dine_in'" class="info-row">
      <UIcon name="i-heroicons-building-storefront" class="info-icon" />
      <div class="info-content">
        <span class="info-label">Ем в зале</span>
        <div class="info-value">{{ branchAddress }}</div>
      </div>
    </div>

    <!-- Срок оплаты (показываем только для неоплаченных/незавершенных заказов) -->
    <div v-if="shouldShowPaymentInfo && paymentExpiresAtFormatted && !isPaymentExpired" class="info-row payment-expires">
      <UIcon name="i-heroicons-exclamation-triangle" class="info-icon" />
      <div class="info-content">
        <span class="info-label">Срок оплаты до</span>
        <div class="info-value">{{ paymentExpiresAtFormatted }}</div>
      </div>
    </div>
    <div v-if="shouldShowPaymentInfo && isPaymentExpired" class="info-row payment-expired">
      <UIcon name="i-heroicons-exclamation-circle" class="info-icon" />
      <div class="info-content">
        <div class="info-value">Время для оплаты истекло</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  formatOrderDate,
  formatPaymentExpiresAt,
  isPaymentExpired as checkPaymentExpired,
 isOrderPaidOrCompleted, ORDER_STATUSES } from '@entities/order';

import type { Order } from '@entities/order';
import { useInstitutionStore } from '@entities/institution';

interface Props {
  order: Order | null;
}

const props = defineProps<Props>();
const institutionStore = useInstitutionStore();

/**
 * Дата создания (поддержка обоих форматов)
 */
const createdAt = computed(() => {
  return props.order?.createdAt || props.order?.created_at || '';
});

/**
 * Тип заказа (поддержка обоих форматов)
 */
const orderType = computed(() => {
  return props.order?.orderType || props.order?.order_type || '';
});

/**
 * Адрес доставки (поддержка обоих форматов)
 */
const deliveryAddress = computed(() => {
  return props.order?.deliveryAddress || props.order?.delivery_address || '';
});

/**
 * Адрес филиала
 * Получает данные из institutionStore по branchId из заказа
 */
const branchAddress = computed(() => {
  // Пытаемся получить данные филиала из заказа (если API возвращает branch)
  const branch = props.order?.branch;
  if (branch?.address) {
    // Если address - строка (для OrderHistoryResponse)
    if (typeof branch.address === 'string') {
      return branch.address;
    }
    // Если address - объект
    const city = branch.address.city || '';
    const street = branch.address.street || '';
    return city && street ? `${city}, ${street}` : city || street;
  }

  // Fallback: ищем филиал в store по branchId
  if (props.order?.branchId) {
    const branchFromStore = institutionStore.branches.find(
      b => String(b.id) === String(props.order?.branchId),
    );

    if (branchFromStore?.address) {
      const city = branchFromStore.address.city || '';
      const street = branchFromStore.address.street || '';
      return city && street ? `${city}, ${street}` : city || street;
    }
  }

  // Если ничего не найдено, возвращаем пустую строку
  return '';
});

/**
 * Форматирует дату создания заказа
 */
const formattedCreatedAt = computed(() => {
  if (!createdAt.value) return '';
  return formatOrderDate(createdAt.value);
});

/**
 * Время истечения оплаты (поддержка обоих форматов)
 */
const paymentExpiresAt = computed(() => {
  return props.order?.paymentExpiresAt || props.order?.payment_expires_at || '';
});

/**
 * Форматирует время истечения платежа
 */
const paymentExpiresAtFormatted = computed(() => {
  if (!paymentExpiresAt.value) return '';
  return formatPaymentExpiresAt(paymentExpiresAt.value);
});

/**
 * Проверяет, истек ли платежный URL
 */
const isPaymentExpired = computed(() => {
  return checkPaymentExpired(props.order);
});

/**
 * Проверяет, нужно ли показывать информацию об оплате
 * Не показываем для завершенных заказов
 */
const shouldShowPaymentInfo = computed(() => {
  if (!props.order) return false

  // Не показываем для завершенных заказов
  return props.order.status !== ORDER_STATUSES.COMPLETED && !isOrderPaidOrCompleted(props.order)
});
</script>

<style scoped lang="scss">
.order-footer {
  @apply grid gap-3 bg-hui-content2 rounded-hui-lg p-4 text-left;
}

.info-row {
  @apply flex items-start gap-3;
}

.info-icon {
  @apply h-5 w-5 shrink-0 mt-0.5 text-hui-default-400;
}

.info-content {
  @apply grid gap-0.5 min-w-0;
}

.info-label {
  @apply text-xs text-hui-default-500;
}

.info-value {
  @apply text-base font-semibold text-hui-foreground;
}

.payment-expires {
  .info-icon,
  .info-label {
    @apply text-hui-warning-600;
  }

  .info-value {
    @apply text-hui-warning-600;
  }
}

.payment-expired {
  .info-icon,
  .info-value {
    @apply text-hui-danger;
  }
}
</style>